import { FC } from 'react';
import { format, parseISO } from 'date-fns';
import { TransactionDetailInfo } from './transactionDetail.types';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, withStyles, ZelleWithdrawTransactionResponse, Card, TransactionType } from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import useElinxDetails from './hooks/useElinxDetails';

const cn = withStyles(styles);

type ZelleTransactionInfoProps = {
  transaction: ZelleWithdrawTransactionResponse;
  fullName: string;
  jwt: string;
  ELinxRequestID?: string;
  transactionDetail: TransactionDetailInfo;
};

const ZelleTransactionInfo: FC<ZelleTransactionInfoProps> = ({
  transaction,
  fullName,
  jwt,
  ELinxRequestID = '',
  transactionDetail,
}) => {
  const { failureReason, accountId, hasErrorInfo } = transactionDetail;

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.DigitalWalletTransactionInfo',
  });

  const { t: tGlobal } = useTranslation('translations');

  const {
    response: { JWT: mainJwt },
  } = useClientData();

  const isMainAccount = !jwt || jwt === mainJwt;

  const walletType = TransactionType.ZELLE_WITHDRAWAL;
  // Zelle is USD-only; fall back to USD until the response's Currency field is populated.
  const currency = transaction.Currency || 'USD';

  const { elinxAdditionalId, invoiceButton } = useElinxDetails({ ELinxRequestID, jwt });

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Date Time' }),
      value: transaction.TransactionDate ? format(parseISO(transaction.TransactionDate), 'yyyy-MM-dd HH:mm:ss') : '-',
    },
    {
      label: t('TransactionDetails.transactionType', { defaultValue: 'Transaction Type' }),
      value: tGlobal(`Global.TransactionTypes.${walletType}`, {
        defaultValue: walletType,
      }),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified ? format(parseISO(transaction.LastModified), 'yyyy-MM-dd HH:mm:ss') : '-',
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      // Zelle payouts have no hold; the response omits HoldAmount, so hard-code 0.00.
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: `${currency} ${formattedCurrency('0')}`,
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
  ];

  const zelleDetails = [
    {
      label: t('ZelleDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value: transaction.RecipientName || fullName || '-',
    },
    {
      label: t('ZelleDetails.recipientEmail', { defaultValue: 'Recipient Email' }),
      value: transaction.RecipientEmail || '-',
      visible: !!transaction.RecipientEmail,
    },
    {
      label: t('ZelleDetails.recipientPhone', { defaultValue: 'Recipient Phone' }),
      value: transaction.RecipientPhone || '-',
      visible: !!transaction.RecipientPhone,
    },
    {
      label: t('ZelleDetails.priorityType', { defaultValue: 'Priority Type' }),
      value: transaction.PriorityType
        ? t(`ZelleDetails.priorityTypes.${transaction.PriorityType}`, { defaultValue: transaction.PriorityType })
        : '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified || '-',
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.errorMessage', { defaultValue: 'Error Message' }),
      value: failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={fullName}
        amount={transaction.Amount}
        currency={currency}
        transactionId={transaction.TransactionID}
        type="Zelle Withdrawal"
        failureReason={failureReason}
        contactId={accountId}
        jwt={jwt}
        isMainAccount={isMainAccount}
        additionalId={elinxAdditionalId}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={walletType}
        jwt={jwt}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('ZelleDetails.title', { defaultValue: 'Zelle Details' })}
        </Title>

        <DetailsRows className={cn('rows')} details={zelleDetails} />
      </Card>

      {hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>

          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default ZelleTransactionInfo;
